import type { SxProps, Theme } from '@mui/material'
import { Autocomplete, Box, Button, Checkbox, Chip, Divider, Paper, Stack, TextField, Typography } from '@mui/material'
import type { HTMLAttributes } from 'react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { convertToRoundedMw } from '@/features/bidding/utils/calculations/convertToRoundedMw'

export type ActivationGroupOption = Readonly<{
  uuid: string
  code: string
  /**
   * Sum of the day's PTU obligations for this AG (watts). Optional —
   * missing when obligation data hasn't loaded or the AG has no OBL.
   */
  oblWatts?: number
}>

export type ActivationGroupMultiSelectProps = Readonly<{
  options: ReadonlyArray<ActivationGroupOption>
  selectedUuids: ReadonlyArray<string>
  onChange: (next: ReadonlyArray<string>) => void
  /** Optional MUI `sx` override merged onto the input wrapper. */
  sx?: SxProps<Theme>
}>

/**
 * Multi-select for activation groups in the bid detail toolbar.
 */
export default function ActivationGroupMultiSelect({
  options,
  selectedUuids,
  onChange,
  sx,
}: ActivationGroupMultiSelectProps) {
  const { t } = useTranslation()

  const codeByUuidMap = useMemo(() => new Map(options.map((option) => [option.uuid, option])), [options])

  // Stable `value` reference prevents a re-render loop: Autocomplete treats a
  // new array as "changed", which compounds with unstable upstream option refs.
  const value = useMemo(
    () =>
      selectedUuids
        .map((uuid) => codeByUuidMap.get(uuid))
        .filter((option): option is ActivationGroupOption => Boolean(option)),
    [selectedUuids, codeByUuidMap],
  )

  const allSelected = options.length > 0 && value.length === options.length

  const handleToggleSelectAll = useCallback(() => {
    onChange(allSelected ? [] : options.map((option) => option.uuid))
  }, [allSelected, onChange, options])

  const renderSelectAllPaper = useCallback(
    ({ children, ...paperProps }: HTMLAttributes<HTMLElement>) => (
      <Paper {...paperProps}>
        <Button
          fullWidth
          disabled={options.length === 0}
          size="small"
          sx={{ justifyContent: 'flex-start', px: 2, py: 1 }}
          // Prevent the input from blurring (which would close the popup
          // before onClick fires). The click still triggers normally.
          onClick={handleToggleSelectAll}
          onMouseDown={(event) => event.preventDefault()}
        >
          {allSelected ? t('bidding.results.unselect_all') : t('bidding.results.select_all', { count: options.length })}
        </Button>
        <Divider />
        {children}
      </Paper>
    ),
    [allSelected, handleToggleSelectAll, options.length, t],
  )

  return (
    <Autocomplete<ActivationGroupOption, true>
      disableCloseOnSelect
      multiple
      getOptionLabel={(option) => option.code}
      isOptionEqualToValue={(option, candidate) => option.uuid === candidate.uuid}
      noOptionsText={t('bidding.results.no_activation_groups')}
      options={[...options]}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('bidding.results.activation_group')}
          placeholder={value.length === 0 ? t('bidding.results.search_activation_groups') : undefined}
          size="small"
        />
      )}
      renderOption={(props, option, { selected }) => (
        <Box component="li" {...props} key={option.uuid}>
          <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap variant="body2">
              {option.code}
            </Typography>
            {option.oblWatts != null && (
              <Typography color="text.secondary" variant="caption">
                {t('bidding.results.obl_label')} {formatMw(option.oblWatts)}
              </Typography>
            )}
          </Stack>
        </Box>
      )}
      renderTags={(picked) => {
        const first = picked[0]
        const extras = picked.length - 1

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {first && <Chip label={first.code} size="small" />}
            {extras > 0 && (
              <Typography color="text.secondary" variant="caption">
                {t('bidding.results.more_count', { count: extras })}
              </Typography>
            )}
          </Box>
        )
      }}
      size="small"
      slots={{ paper: renderSelectAllPaper }}
      sx={[{ minWidth: 320 }, ...(Array.isArray(sx) ? sx : [sx])]}
      value={value}
      onChange={(_, picked) => onChange(picked.map((option) => option.uuid))}
    />
  )
}

function formatMw(watts: number): string {
  return `${convertToRoundedMw(watts).toFixed(1)} MW`
}
